import { type FormEvent, useState } from 'react';
import { useApi } from '@plataforma/web/api';
import { fechaHoraLocal, mxn } from '../formato';
import type { Vehiculo } from '../paginas/tipos';

export interface Semaforo {
  color: 'importable' | 'revisar' | 'no_importable';
  motivos: string[];
  arancelPorcentaje: string | null;
  anio: number | null;
  antiguedad: number | null;
  origen: string;
  estimacion: {
    valorAduanaMxn: string;
    igiMxn: string;
    dtaMxn: string;
    ivaMxn: string;
    prevalidacionMxn: string;
    totalMxn: string;
  } | null;
  /** Solo en la consulta del semáforo (la puja máxima no los trae). */
  requisitos?: string[];
  /** 2.15: vía elegida entre las publicadas, si el valor en aduana es aproximado y las vías probadas. */
  tipoImportacion?: string | null;
  aproximado?: boolean;
  cotizacionId?: string | null;
  vias?: {
    tipo: string;
    nombre: string;
    semaforo: { color: string; motivos: string[] };
    sinRegla: boolean;
    clavesPedimento?: { clave: string; nombre: string }[];
  }[];
}

const COLORES = {
  importable: { texto: 'Importable', clase: 'exito' },
  revisar: { texto: 'Revisar', clase: 'aviso' },
  no_importable: { texto: 'No importable', clase: 'peligro' },
} as const;

/** Resultado del semáforo (arquitectura, sección 7): regla vigente en la fecha estimada de cruce. */
export function ResultadoSemaforo({ s }: { s: Semaforo }) {
  const c = COLORES[s.color];
  return (
    <div className="cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <span className={`etiqueta ${c.clase}`} style={{ fontSize: 14, padding: '4px 12px' }}>
          {c.texto}
        </span>{' '}
        <span className="subtitulo">
          Origen {s.origen} · {s.anio ?? 'año ?'} · antigüedad {s.antiguedad ?? '?'} años
          {s.arancelPorcentaje ? ` · arancel ${s.arancelPorcentaje} %` : ''}
        </span>
      </div>
      {s.motivos.length > 0 && (
        <ul style={{ margin: 0 }}>
          {s.motivos.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      {s.estimacion && (
        <dl className="resumen">
          {(
            [
              ['Valor en aduana', s.estimacion.valorAduanaMxn],
              ['IGI (arancel)', s.estimacion.igiMxn],
              ['DTA', s.estimacion.dtaMxn],
              ['IVA', s.estimacion.ivaMxn],
              ['Prevalidación', s.estimacion.prevalidacionMxn],
              ['Total estimado', s.estimacion.totalMxn],
            ] as const
          ).map(([dt, dd]) => (
            <div key={dt}>
              <dt>{dt}</dt>
              <dd>{mxn(dd)}</dd>
            </div>
          ))}
        </dl>
      )}
      {(s.requisitos ?? []).length > 0 && (
        <p className="subtitulo">Requisitos: {s.requisitos?.join(' · ')}</p>
      )}
      {s.tipoImportacion && (
        <p className="subtitulo">
          Vía elegida:{' '}
          <strong>
            {s.vias?.find((v) => v.tipo === s.tipoImportacion)?.nombre ?? s.tipoImportacion}
          </strong>
          {s.cotizacionId
            ? ' · valor en aduana de la cotización del agente'
            : s.aproximado
              ? ' · valor en aduana aproximado (sin cotización del agente)'
              : ''}
        </p>
      )}
      {(s.vias ?? []).length > 1 && (
        <ul style={{ margin: 0 }}>
          {s.vias?.map((v) => (
            <li key={v.tipo}>
              {v.nombre}
              {v.clavesPedimento?.length
                ? ` (pedimento ${v.clavesPedimento.map((x) => x.clave).join('/')})`
                : ''}
              : {v.semaforo.color}
              {v.sinRegla ? ' (sin regla publicada)' : ''}
            </li>
          ))}
        </ul>
      )}
      <p className="subtitulo">
        Estimación con la regla vigente; las reglas de importación se confirman con el agente
        aduanal.
      </p>
    </div>
  );
}

/** Semáforo de importación del vehículo y su uso como costo estimado de importación. */
export function ImportacionVehiculo({ v, alRegistrar }: { v: Vehiculo; alRegistrar: () => void }) {
  const api = useApi();
  const [s, setS] = useState<Semaforo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [registrado, setRegistrado] = useState(false);
  const enDosSemanas = fechaHoraLocal(new Date(Date.now() + 14 * 86_400_000)).slice(0, 10);
  const consultar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setError(null);
    setAviso(null);
    try {
      setRegistrado(false);
      setS(
        await api<Semaforo>('POST', '/sourcing/importacion/semaforo', {
          vin: v.vin,
          ...(v.anio && { anio: v.anio }),
          titulo: v.titulo,
          region: f.get('region'),
          fechaCruce: f.get('fechaCruce'),
          ...(v.marca && { marca: v.marca }),
          ...(v.modelo && { modelo: v.modelo }),
          ...(v.version && { version: v.version }),
          ...(f.get('valorAduanaMxn') && { valorAduanaMxn: String(f.get('valorAduanaMxn')) }),
          ...(f.get('valorNadaMxn') && { valorNadaMxn: String(f.get('valorNadaMxn')) }),
          robado: f.get('robado') === 'on',
        }),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const registrar = async () => {
    if (!s?.estimacion || registrado) return;
    setRegistrado(true);
    try {
      await api('POST', `/finance/vehiculos/${v.id}/costos`, {
        etapa: 'importacion',
        concepto: 'Importación estimada (semáforo)',
        cantidad: s.estimacion.totalMxn,
        moneda: 'MXN',
        fecha: fechaHoraLocal(new Date()).slice(0, 10),
        estimado: true,
      });
      setAviso('Registrado como costo estimado de importación; el pedimento real lo reemplazará.');
      alRegistrar();
    } catch (err) {
      setRegistrado(false);
      setError((err as Error).message);
    }
  };
  return (
    <section className="seccion">
      <header>
        <h2>Importación</h2>
      </header>
      <form className="cuerpo formulario" onSubmit={(e) => void consultar(e)}>
        <label className="campo">
          <span>Región de destino</span>
          <select name="region" defaultValue="frontera">
            <option value="frontera">Frontera norte</option>
            <option value="resto">Resto del país</option>
          </select>
        </label>
        <label className="campo">
          <span>Fecha estimada de cruce</span>
          <input name="fechaCruce" type="date" required defaultValue={enDosSemanas} />
        </label>
        <label className="campo">
          <span>Valor en aduana (MXN, opcional)</span>
          <input
            name="valorAduanaMxn"
            inputMode="decimal"
            pattern="\d{1,12}(\.\d{1,2})?"
            placeholder="Para estimar impuestos"
          />
        </label>
        <label className="campo">
          <span>Valor NADA (clásicos, MXN, opcional)</span>
          <input
            name="valorNadaMxn"
            inputMode="decimal"
            pattern="\d{1,12}(\.\d{1,2})?"
            placeholder="Autos de 30 años o más"
          />
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="robado" type="checkbox" style={{ minHeight: 0 }} />
          <span>Tiene reporte de robo</span>
        </label>
        <div className="acciones">
          <button className="boton">Consultar semáforo</button>
        </div>
      </form>
      {error && (
        <p className="error" style={{ margin: 16 }}>
          {error}
        </p>
      )}
      {s && <ResultadoSemaforo s={s} />}
      {s?.estimacion && (
        <div
          className="cuerpo"
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            borderTop: '1px solid var(--borde-suave)',
          }}
        >
          <button className="boton primario" disabled={registrado} onClick={() => void registrar()}>
            Registrar {mxn(s.estimacion.totalMxn)} como costo estimado
          </button>
          {aviso && <span className="subtitulo">{aviso}</span>}
        </div>
      )}
    </section>
  );
}
